import { getComputedDisplayName } from "../common";
import { ReactElement, useMemo } from "react";
import { WorkspaceContactDetailsModel } from "client/Api/Email/contacts";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { PersonalInformationPage } from "client/Features/PersonalInformationPage/PersonalInformationPage";
import { GeneralInformationSection } from "./Sections/GeneralInformationSection";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { MultilineTextSection } from "./Sections/MultilineTextSection";
import { WorkingDaysSection } from "./Sections/WorkingDaysSection";
import { TagsSection } from "./Sections/TagsSection";
import { CommunicationButtons } from "../CommunicationButtons/CommunicationButtons";
import { PersonAvatarStatusKind, PersonAvatarStatusValue } from "client/Components";
import { presenceToAvatarStatusMap } from "../PersonCard/PersonCard";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";
import { getIdentifiesAs } from "client/Features/Settings/UserProfile/UserProfileForm";

export type DetailsForWorkspaceProps = {
	contact: WorkspaceContactDetailsModel;
};

export const DetailsForWorkspace = (props: DetailsForWorkspaceProps): ReactElement => {
	const {
		contact
	} = props;

	const {
		emailAddress,
		photoUrl,
		backgroundImageUrl,
		phone,
		jobTitle,
		department,
		officeLocation,
		presenceStatus,
		aboutMe,
		birthday: birthdayAsString,
		workingDays,
		projects,
		skills,
		hobbies,
		education,
		pronouns,
		customString1,
		customString2,
		customString3,
		customString4,
		customString5
	} = contact;

	const userTimezone: TimeZone = useUserTimezone();

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(contact);

	const info = useMemo(
		(): string => {
			const sections: string[] = [
				jobTitle,
				department,
				officeLocation
			]
				.filter(isNotNilOrEmpty);

			return sections.join(" | ");
		},
		[
			department,
			jobTitle,
			officeLocation
		]
	);

	const status = useMemo(
		(): PersonAvatarStatusValue | undefined => {
			const kind: PersonAvatarStatusKind | undefined = presenceToAvatarStatusMap[presenceStatus];

			if (isNil(kind)) {
				return undefined;
			}

			return {
				kind: "status",
				status: kind
			};
		},
		[presenceStatus]
	);

	const birthday = useMemo(
		(): DateOnly | null => {
			return parseDtoDateOnlyIgnoringOffset(birthdayAsString);
		},
		[birthdayAsString]
	);

	const identifiesAs = useMemo(
		(): string | null => getIdentifiesAs(pronouns),
		[pronouns]
	);

	return (
		<PersonalInformationPage
			trackingId="workspace-contact"
		>
			<PersonalInformationPage.Banner
				fullName={computedDisplayName}
				identifiesAs={identifiesAs}
				trackingId="banner"
				avatarUrl={photoUrl ?? undefined}
				avatarPlaceholder={{ kind: "initials", initials }}
				backgroundImageUrl={backgroundImageUrl ?? undefined}
				info={info}
				actions={(
					<CommunicationButtons
						emailAddress={emailAddress}
						phoneNumber={phone}
						variant="details"
					/>
				)}
				status={status}
			/>
			<GeneralInformationSection
				birthday={birthday}
				emailAddresses={[emailAddress].filter(isNotNil)}
				phoneNumbers={{
					mobilePhone: phone
				}}
				timezone={userTimezone}
				emptyBehavior="not-render"
				areContactLinksEnabled
				isCopyToClipboardEnabled
				customString1={customString1}
				customString2={customString2}
				customString3={customString3}
				customString4={customString4}
				customString5={customString5}
			/>
			<MultilineTextSection
				label={localize("_AboutMe")}
				trackingId="about-me"
				text={aboutMe}
				emptyBehavior="not-render"
			/>
			<WorkingDaysSection
				workingDays={workingDays}
				emptyBehavior="not-render"
			/>
			<TagsSection
				title={localize("_Projects")}
				trackingId="projects"
				tags={projects}
				emptyBehavior="not-render"
			/>
			<TagsSection
				title={localize("_SkillsAndExpertise")}
				trackingId="skills-and-expertise"
				tags={skills}
				emptyBehavior="not-render"
			/>
			<TagsSection
				title={localize("_SchoolsAndEducation")}
				trackingId="schools-and-education"
				tags={education}
				emptyBehavior="not-render"
			/>
			<TagsSection
				title={localize("_InterestsAndHobbies")}
				trackingId="interests-and-hobbies"
				tags={hobbies}
				emptyBehavior="not-render"
			/>
		</PersonalInformationPage>
	);
};
DetailsForWorkspace.displayName="DetailsForWorkspace";